"use client";

import { Button, Modal } from "antd";
import { usePathname } from "next/navigation";
import { FormattedMessage } from "react-intl";
import { useCallback, useEffect, useState } from "react";
import styles from "./SiteMessageNotifier.module.css";
import { useLocale } from "@/context/LocaleContext";

interface ISiteMessage {
  id: string;
  version: string;
  marker: string;
  title: string;
  text: string;
}

interface IRawSiteMessage {
  id?: unknown;
  version?: unknown;
  title?: unknown;
  text?: unknown;
}

const SITE_MESSAGES_URL = "/site-messages/messages.json";
const READ_MESSAGES_STORAGE_KEY = "ui:read-site-messages:v1";
const SITE_MESSAGES_POLL_INTERVAL_MS = 10 * 60 * 1000;

function normalizePlainText(value: unknown): string | undefined {
  if (typeof value != "string") return undefined;
  const trimmed = value.trim();
  if (trimmed.length == 0) return undefined;
  return trimmed;
}

function resolveLocalizedText(
  value: unknown,
  locale: "en" | "ru"
): string | undefined {
  const direct = normalizePlainText(value);
  if (direct) return direct;

  if (!value || typeof value != "object") return undefined;

  const localized = value as Record<string, unknown>;
  const fromCurrentLocale = normalizePlainText(localized[locale]);
  if (fromCurrentLocale) return fromCurrentLocale;

  const fallbackLocale = locale == "ru" ? "en" : "ru";
  const fromFallbackLocale = normalizePlainText(localized[fallbackLocale]);
  if (fromFallbackLocale) return fromFallbackLocale;

  for (const nextValue of Object.values(localized)) {
    const normalized = normalizePlainText(nextValue);
    if (normalized) return normalized;
  }

  return undefined;
}

function toSiteMessage(
  raw: IRawSiteMessage,
  locale: "en" | "ru"
): ISiteMessage | undefined {
  if (
    typeof raw.id != "string" ||
    (typeof raw.version != "string" && typeof raw.version != "number") ||
    raw.title == undefined ||
    raw.text == undefined
  ) {
    return undefined;
  }

  const id = raw.id.trim();
  const version =
    typeof raw.version == "number"
      ? String(raw.version)
      : raw.version.trim();
  const title = resolveLocalizedText(raw.title, locale);
  const text = resolveLocalizedText(raw.text, locale);
  if (
    id.length == 0 ||
    version.length == 0 ||
    title == undefined ||
    text == undefined
  ) {
    return undefined;
  }

  return {
    id,
    version,
    marker: `${id}@${version}`,
    title,
    text,
  };
}

function normalizeMessages(
  payload: unknown,
  locale: "en" | "ru"
): ISiteMessage[] {
  const normalized = new Map<string, ISiteMessage>();

  const appendMessage = (rawItem: unknown) => {
    const message = toSiteMessage((rawItem ?? {}) as IRawSiteMessage, locale);
    if (!message) return;
    if (normalized.has(message.marker)) return;
    normalized.set(message.marker, message);
  };

  if (Array.isArray(payload)) {
    for (const item of payload) {
      appendMessage(item);
    }
    return Array.from(normalized.values());
  }

  if (!payload || typeof payload != "object") {
    return [];
  }

  const asRecord = payload as Record<string, unknown>;
  if (Array.isArray(asRecord.messages)) {
    for (const item of asRecord.messages) {
      appendMessage(item);
    }
    return Array.from(normalized.values());
  }

  const maybeSingle = toSiteMessage(asRecord as IRawSiteMessage, locale);
  if (maybeSingle) {
    normalized.set(maybeSingle.marker, maybeSingle);
  }
  return Array.from(normalized.values());
}

function readSeenMessages(): Set<string> {
  if (typeof window == "undefined") return new Set<string>();

  try {
    const raw = localStorage.getItem(READ_MESSAGES_STORAGE_KEY);
    if (!raw) return new Set<string>();

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set<string>();

    const onlyStrings = parsed.filter(
      (item): item is string => typeof item == "string" && item.length > 0
    );
    return new Set<string>(onlyStrings);
  } catch {
    return new Set<string>();
  }
}

function writeSeenMessages(seenMarkers: Set<string>): void {
  if (typeof window == "undefined") return;

  try {
    if (seenMarkers.size == 0) {
      localStorage.removeItem(READ_MESSAGES_STORAGE_KEY);
      return;
    }
    localStorage.setItem(
      READ_MESSAGES_STORAGE_KEY,
      JSON.stringify(Array.from(seenMarkers))
    );
  } catch {
    // Ignore storage limitations in private mode.
  }
}

function reconcileSeenMessages(activeMessages: ISiteMessage[]): Set<string> {
  const activeMarkers = new Set(activeMessages.map((message) => message.marker));
  if (activeMarkers.size == 0) {
    writeSeenMessages(new Set<string>());
    return new Set<string>();
  }

  const seenBefore = readSeenMessages();
  const seenActual = new Set<string>();
  for (const marker of seenBefore) {
    if (activeMarkers.has(marker)) {
      seenActual.add(marker);
    }
  }

  if (seenActual.size != seenBefore.size) {
    writeSeenMessages(seenActual);
  }

  return seenActual;
}

export default function SiteMessageNotifier() {
  const { locale } = useLocale();
  const pathname = usePathname();
  const [messages, setMessages] = useState<ISiteMessage[]>([]);
  const [openedMessageMarker, setOpenedMessageMarker] = useState<
    string | undefined
  >(
    undefined
  );

  const openedMessage = openedMessageMarker
    ? messages.find((message) => message.marker == openedMessageMarker)
    : undefined;

  const loadMessages = useCallback(async () => {
    let payload: unknown;
    try {
      const response = await fetch(SITE_MESSAGES_URL, { cache: "no-store" });
      if (!response.ok) return;
      payload = await response.json();
    } catch {
      // Keep existing message-read markers unchanged on fetch/parse error.
      return;
    }

    const activeMessages = normalizeMessages(payload, locale);
    const seenMessages = reconcileSeenMessages(activeMessages);
    const unread = activeMessages.find(
      (message) => !seenMessages.has(message.marker)
    );

    setMessages(activeMessages);
    setOpenedMessageMarker(unread?.marker);
  }, [locale]);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages, pathname]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      void loadMessages();
    }, SITE_MESSAGES_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(timerId);
    };
  }, [loadMessages]);

  const handleAcknowledge = () => {
    if (!openedMessage) return;

    const seenMessages = readSeenMessages();
    seenMessages.add(openedMessage.marker);
    writeSeenMessages(seenMessages);

    const nextUnread = messages.find(
      (message) => !seenMessages.has(message.marker)
    );
    setOpenedMessageMarker(nextUnread?.marker);
  };

  const handleDismissWithoutAck = () => {
    setOpenedMessageMarker(undefined);
  };

  return (
    <Modal
      open={openedMessage != undefined}
      title={openedMessage?.title}
      onCancel={handleDismissWithoutAck}
      footer={[
        <Button key="acknowledge" type="primary" onClick={handleAcknowledge}>
          <FormattedMessage id="title-got-it" />
        </Button>,
      ]}
    >
      <p className={styles.modalText}>{openedMessage?.text}</p>
    </Modal>
  );
}
